import { useState, useEffect } from 'react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, DollarSign, Package, AlertTriangle, Activity, Wrench, Gem, Clock, ArrowRight, TrendingDown, Box } from 'lucide-react';
import './Dashboard.css';

const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'min' : 'mins'} ago`;
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hr' : 'hrs'} ago`;
  if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
};

interface LivePulse {
  sales_today: { count: number; revenue: number };
  sales_yesterday: { count: number; revenue: number };
  tpl_assembling_pieces: number;
  urgent_work_orders: number;
  top_bestsellers: Array<{ sku: string; nombre: string; ventas_14d: number; current_stock: number; probability30d: number }>;
}

export default function Dashboard() {
  const { api } = useApi();
  const { showError } = useToast();
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      const response = await api.get('/dashboard/metrics');
      setMetrics(response.data);
    } catch (err: any) {
      showError('Error al cargar métricas del Live Pulse');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="dashboard-loading-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: '20px' }}>
        <div className="spinner" style={{ width: '40px', height: '40px', border: '3px solid rgba(0,0,0,0.1)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: 'var(--text-secondary)' }}>Cargando Panel de Mando...</p>
      </div>
    );
  }

  if (!metrics || !metrics.live_pulse) return null;

  const pulse = metrics.live_pulse as LivePulse;
  
  // Calculate today vs yesterday trends
  const revDiff = pulse.sales_today.revenue - pulse.sales_yesterday.revenue;
  const revPercentStr = pulse.sales_yesterday.revenue > 0 ? ((revDiff / pulse.sales_yesterday.revenue) * 100).toFixed(1) : '100';
  const isRevUp = revDiff >= 0;

  const countDiff = pulse.sales_today.count - pulse.sales_yesterday.count;
  const countPercentStr = pulse.sales_yesterday.count > 0 ? ((countDiff / pulse.sales_yesterday.count) * 100).toFixed(1) : '100';
  const isCountUp = countDiff >= 0;

  return (
    <div className="modern-dashboard-container" style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto', background: '#f8fafc', minHeight: '100vh', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Activity color="var(--primary)" /> Live Pulse 
          </h1>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '14px' }}>Monitor de operativa diaria en tiempo real</p>
        </div>
      </div>

      {/* METRICS FLASH ROW */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '32px' }}>
        
        {/* Card: Ventas Hoy (Dinero) */}
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#059669' }}>
               <DollarSign size={24} />
            </div>
            <div style={{ padding: '4px 8px', borderRadius: '20px', background: isRevUp ? '#ecfdf5' : '#fef2f2', color: isRevUp ? '#059669' : '#dc2626', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                {isRevUp ? <TrendingUp size={14} /> : <TrendingDown size={14} />} {Math.abs(Number(revPercentStr))}% {isRevUp ? 'vs ayer' : 'vs ayer'}
            </div>
          </div>
          <div>
             <h3 style={{ margin: 0, fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Ingresos HOY</h3>
             <p style={{ margin: '4px 0 0 0', fontSize: '28px', fontWeight: 700, color: '#0f172a' }}>
               ${pulse.sales_today.revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
             </p>
          </div>
        </div>

        {/* Card: Pedidos Hoy (Cantidad) */}
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb' }}>
               <Package size={24} />
            </div>
            <div style={{ padding: '4px 8px', borderRadius: '20px', background: isCountUp ? '#ecfdf5' : '#fef2f2', color: isCountUp ? '#059669' : '#dc2626', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                {isCountUp ? <TrendingUp size={14} /> : <TrendingDown size={14} />} {Math.abs(Number(countPercentStr))}% vs ayer
            </div>
          </div>
          <div>
             <h3 style={{ margin: 0, fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Pedidos HOY</h3>
             <p style={{ margin: '4px 0 0 0', fontSize: '28px', fontWeight: 700, color: '#0f172a' }}>
               {pulse.sales_today.count} <span style={{fontSize: '14px', fontWeight: 400, color: '#94a3b8'}}>unidades</span>
             </p>
          </div>
        </div>

        {/* Card: 3PL Fase Montaje */}
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d97706' }}>
               <Box size={24} />
            </div>
          </div>
          <div>
             <h3 style={{ margin: 0, fontSize: '14px', color: '#64748b', fontWeight: 500 }}>En Producción P/USA (3PL)</h3>
             <p style={{ margin: '4px 0 0 0', fontSize: '28px', fontWeight: 700, color: '#0f172a' }}>
               {pulse.tpl_assembling_pieces} <span style={{fontSize: '14px', fontWeight: 400, color: '#94a3b8'}}>piezas</span>
             </p>
          </div>
        </div>

        {/* Card: OF Urgentes */}
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: pulse.urgent_work_orders > 0 ? '#fef2f2' : '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: pulse.urgent_work_orders > 0 ? '#dc2626' : '#94a3b8' }}>
               <AlertTriangle size={24} />
            </div>
          </div>
          <div>
             <h3 style={{ margin: 0, fontSize: '14px', color: '#64748b', fontWeight: 500 }}>OFs Urgentes/Alta Prioridad</h3>
             <p style={{ margin: '4px 0 0 0', fontSize: '28px', fontWeight: 700, color: pulse.urgent_work_orders > 0 ? '#dc2626' : '#0f172a' }}>
               {pulse.urgent_work_orders} <span style={{fontSize: '14px', fontWeight: 400, color: '#94a3b8'}}>órdenes abiertas</span>
             </p>
          </div>
        </div>
      </div>

      {/* BODY GRID */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: '24px', alignItems: 'start' }}>
         
         <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
           
           {/* SALES CHART */}
           <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', color: '#0f172a', fontWeight: 600 }}>Evolución de Ventas (14 Días)</h3>
              </div>
              <div style={{ height: '300px', width: '100%' }}>
                <ResponsiveContainer>
                  <AreaChart data={metrics.sales_trends.slice(-14)} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15}/>
                        <stop offset="95%" stopColor="#2563eb" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: '#64748b', fontSize: 12 }} 
                      tickFormatter={(val) => val.split('-').slice(1).join('/')}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fill: '#64748b', fontSize: 12 }} 
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip 
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
                      labelStyle={{ color: '#64748b', marginBottom: '4px' }}
                    />
                    <Area type="monotone" dataKey="total_quantity" name="Unidades Vendidas" stroke="#2563eb" strokeWidth={3} fillOpacity={1} fill="url(#colorSales)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
           </div>

           {/* TOP BESTSELLERS WITH PROBABILITY */}
           <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', color: '#0f172a', fontWeight: 600 }}>Top 5 En Tendencia (Últimos 14d)</h3>
                <a href="#stock-metal-pieces" style={{ fontSize: '13px', color: '#2563eb', textDecoration: 'none', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px' }}>Ver Catálogo <ArrowRight size={14} /></a>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {pulse.top_bestsellers.map((item, idx) => {
                  const isStockLow = item.current_stock < (item.ventas_14d * 2); // Un pequeño cálculo rápido
                  return (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
                         <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: idx === 0 ? '#fef3c7' : '#f1f5f9', color: idx === 0 ? '#d97706' : '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '14px' }}>
                           #{idx + 1}
                         </div>
                         <div>
                           <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{item.nombre || item.sku}</p>
                           <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#64748b' }}>{item.sku}</p>
                         </div>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                        <div style={{ textAlign: 'right' }}>
                          <p style={{ margin: 0, fontSize: '12px', color: '#64748b', fontWeight: 500 }}>Ventas 14d</p>
                          <p style={{ margin: '2px 0 0 0', fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{item.ventas_14d} und</p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <p style={{ margin: 0, fontSize: '12px', color: '#64748b', fontWeight: 500 }}>P. de Venta (30d)</p>
                          <p style={{ margin: '2px 0 0 0', fontSize: '14px', fontWeight: 700, color: item.probability30d >= 80 ? '#059669' : item.probability30d >= 40 ? '#d97706' : '#dc2626' }}>{item.probability30d}%</p>
                        </div>
                        <div style={{ textAlign: 'right', minWidth: '70px' }}>
                          <p style={{ margin: 0, fontSize: '12px', color: '#64748b', fontWeight: 500 }}>Stock Actual</p>
                          <p style={{ margin: '2px 0 0 0', fontSize: '14px', fontWeight: 700, color: isStockLow ? '#dc2626' : '#0f172a' }}>{item.current_stock}</p>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
           </div>

         </div>

         {/* RIGHT COLUMN: RECENT ACTIVITY TIMELINE */}
         <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', minHeight: '600px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#0f172a', fontWeight: 600 }}>Registro de Actividad</h3>
              <a href="#admin-log" style={{ fontSize: '13px', color: '#64748b', textDecoration: 'none' }}>Ver histórico completo</a>
            </div>

            <div style={{ position: 'relative', paddingLeft: '8px' }}>
              {/* Línea vertical de timeline */}
              <div style={{ position: 'absolute', top: '16px', bottom: '16px', left: '20px', width: '2px', background: '#f1f5f9' }}></div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {metrics.recent_activity.map((activity: any, idx: number) => {
                  const date = new Date(activity.created_at);
                  const isFirst = idx === 0;

                  return (
                    <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', position: 'relative', zIndex: 1 }}>
                      <div style={{ 
                         width: '26px', height: '26px', borderRadius: '50%', 
                         background: isFirst ? '#eff6ff' : 'white', 
                         border: `2px solid ${isFirst ? '#2563eb' : '#e2e8f0'}`,
                         display: 'flex', alignItems: 'center', justifyContent: 'center',
                         boxShadow: isFirst ? '0 0 0 4px rgba(37, 99, 235, 0.1)' : 'none',
                         marginTop: '2px'
                      }}>
                         <Clock size={12} color={isFirst ? '#2563eb' : '#94a3b8'} />
                      </div>
                      <div>
                        <div style={{ fontSize: '13px', color: '#0f172a', fontWeight: 500, lineHeight: '1.4' }}>
                          {activity.description || activity.action}
                        </div>
                        <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px', display: 'flex', gap: '8px' }}>
                          <span>hace {formatRelativeTime(date).replace(' ago', '')}</span>
                          {activity.username && <span>• {activity.username}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
         </div>
         
      </div>

    </div>
  );
}
